import {ChangeDetectionStrategy, Component, computed, inject, Signal} from "@angular/core";
import {AnalyticsService} from '../../logging/analytics.service';
import {navParam} from "../../navigation/navigation-params";
import {Page} from "../../navigation/page";
import {FedeltaImageData} from "@fedelta-pos/data-client";
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence} from "../../buttons/button/button";
import {MenuItemData} from '../../menu/menu-entity.model';
import {MenuModel} from '../../menu/menu.model';
import {AppPromoAction, AppPromoData} from '../../identity/member-summary-entity.model';
import {NgClass} from '@angular/common';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {DietaryPanelComponent} from '../../order/order-item/dietary-panel/dietary-panel.component';
import {ImageCacheDirective} from '../../ui-elements/image/image-cache.directive';
import {UrlPipe} from '../../utils/url.pipe';
import {HtmlPipe} from '../../utils/html.pipe';
import {CurrencyPipe} from '../../utils/currency.pipe';
import {isFedeltaImageData} from '../../utils/string-utils';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';
import {ActivatableDirective} from '../../ui-elements/activatable.directive';

class ImageSpec {
    constructor(public image: string | FedeltaImageData, public autoCropImage: boolean) {
    }
}

@Component({
    selector: "woa-promo-page",
    templateUrl: "./promo-page.component.html",
    styleUrl: "./promo-page.component.scss",
    providers: [{provide: Page, useExisting: PromoPageComponent}],
    imports: [
        ButtonComponent,
        CurrencyPipe,
        DietaryPanelComponent,
        HtmlPipe,
        ImageCacheDirective,
        ModalPageLayoutComponent,
        NgClass,
        PageFooterControlDirective,
        UrlPipe,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromoPageComponent extends Page {
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public menuModel: MenuModel = inject(MenuModel);

    ButtonProminence = ButtonProminence;
    isFedeltaImageData = isFedeltaImageData;

    public readonly promo: Signal<AppPromoData> = navParam.required<AppPromoData>("promo");
    public readonly menuItem: Signal<MenuItemData> = navParam<MenuItemData>("menuItem");

    public readonly informational: Signal<boolean> = computed<boolean>(() =>
        (this.promo().popupAction === AppPromoAction.SHOW_PRODUCTS && this.promo().promotedCategoryInformational) || this.menuItem()?.informational);

    public readonly title: Signal<string> = computed(() =>
        this.promo().appText1 ? "woa.heading.promo.appText"
            : this.menuItem() ? "woa.heading.promo.default"
                : "woa.heading.promo.name");

    public readonly imageSpec: Signal<ImageSpec> = computed(() => {
        if (this.promo().image.id) {
            return new ImageSpec(this.promo().image, this.promo().imageAutoCrop);
        }
        if (this.menuItem()) {
            const image: FedeltaImageData = this.menuModel.itemImage()(undefined, this.menuItem(), true);
            if (image) {
                return new ImageSpec(image, this.promo().imageAutoCrop);
            }
        }
        if (this.configurationModel.app()?.defaultImage) {
            return new ImageSpec("data:image/png;base64," + this.configurationModel.app()?.defaultImage, false);
        }
        return new ImageSpec(undefined, false);
    });

    public readonly hasAction: Signal<boolean> = computed(() => this.menuItem() && !!this.promo().popupAction);

    constructor() {
        super();
        void this.analyticsService.trackPromoShown(this.promo() ? this.promo().name : undefined, "popup",
            this.menuModel.getMenuItemDisplayName(this.menuItem()), this.menuItem()?.sellable.defaultPrice, this.menuItem()?.sellable.plu);
    }

    override onDidDismissPage(actioned: boolean): void {
        if (this.hasAction() && !actioned) {
            void this.analyticsService.trackPromoCancelled(this.promo() ? this.promo().name : undefined, "popup",
                this.menuModel.getMenuItemDisplayName(this.menuItem()), this.menuItem()?.sellable.defaultPrice, this.menuItem()?.sellable.plu);
        }
    }

    async performAction(onlyWhenNoButton: boolean = false): Promise<void> {
        if (this.promo() && !this.promo().popupAction) {
            return;
        }
        if (onlyWhenNoButton && this.promo() && this.promo().showActionButtonOnPopup) {
            return;
        }
        if (this.promo()) {
            switch (this.promo().popupAction) {
                case AppPromoAction.ORDER_PRODUCT:
                    void this.analyticsService.trackPromoItemOrdered(this.promo().name, "popup",
                        this.menuModel.getMenuItemDisplayName(this.menuItem()), this.menuItem()?.sellable.defaultPrice, this.menuItem()?.sellable.plu);
                    break;
                case AppPromoAction.SHOW_PRODUCTS:
                    void this.analyticsService.trackPromoItemsShown(this.promo().name, "popup");
                    break;
            }
        } else {
            void this.analyticsService.trackPromoItemOrdered(undefined, "popup",
                this.menuModel.getMenuItemDisplayName(this.menuItem()), this.menuItem()?.sellable.defaultPrice, this.menuItem()?.sellable.plu);
        }
        void this.dismiss(true);
    }
}
